import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Page, alert } from 'equasion-lib';
import { FadeLoader } from 'react-spinners';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiLock,
  FiNavigation,
  FiPackage,
  FiX,
  FiXCircle
} from 'react-icons/fi';
import ZoneScanButton from '@/lib/shared-coms/ZoneScanner';
import {
  STALE_PICK_CLAIM_MINUTES,
  addTotePathForPick,
  companyUsesPickVerifyShip,
  salesOrdersTabPath
} from '@/lib/pickVerifyShip';
import {
  combineAlertOrders,
  combineOrderPath,
  combineParent,
  combinePartners,
  soLabel,
  soList,
  type CombineContext
} from '@/lib/combineShipment';
import { CombineShipmentStep, type CombineStep } from '@/lib/shared-coms/CombineShipment';
import PickLineRow from './com/PickLineRow/PickLineRow';
import PickLineSheet from './com/PickLineSheet/PickLineSheet';
import PickShortReasonForm from './com/PickShortReasonForm/PickShortReasonForm';
import type { PickBusy } from './SalesOrderPick.data';
import {
  isPickLineLocked,
  pickClaimView,
  pickCloseLineLabel,
  pickCompleteText,
  pickLineAtStop,
  pickLineNeedsClose,
  pickLockedLineCopy,
  pickLockedLinesNote,
  pickLineScansUnits,
  pickNoStockCopy,
  pickRowShortFound,
  pickScannedCount,
  pickUnitScanResumeZone,
  pickShortsConfirmText,
  pickShowAddTote,
  pickWalkRows,
  type PickActionResult,
  type PickCompletion,
  type PickConfirmLineArgs,
  type PickLine,
  type PickList,
  type PickQuarantineDamagedArgs,
  type PickQuarantineMissingArgs,
  type PickQuarantineResult,
  type PickScanUnitArgs,
  type PickWalkStop
} from './pickLineSteps';
import './SalesOrderPick.scss';

type SalesOrderPickProps = {
  list: PickList;
  company: {
    id: string;
    usesTotes?: boolean | null;
    fulfillmentWorkflow?: string | null;
  } | null;
  companyId: string;
  setStartZoneId: (zoneId: string | null) => void;
  busy: PickBusy;
  startError: string | null;
  onStart: (takeOver: boolean) => void;
  onConfirmLine: (args: PickConfirmLineArgs) => Promise<PickActionResult>;
  /** A unit-scan (controlled) line sends its units scanned and a `reason` (required). */
  onMarkShort: (
    orderItemId: string,
    foundQuantity: number,
    options?: { blocking?: boolean; reason?: string | null }
  ) => Promise<PickActionResult>;
  /** A unit-scan (controlled) line: one more unit scanned at a confirmed zone (from the line sheet). */
  onScanUnit?: (args: PickScanUnitArgs) => Promise<PickActionResult>;
  /** A unit-scan (controlled) line: take a scanned unit back off — that serial, or the last. */
  onRemoveUnit?: (orderItemId: string, serialNumber?: string | null) => Promise<PickActionResult>;
  /** "Couldn't find them all here": fewer than the zone's share at a confirmed zone. */
  onMarkStopShort: (
    orderItemId: string,
    zoneId: string,
    foundQuantity: number
  ) => Promise<PickActionResult>;
  /** Undo the line — or, with `zoneId`, only that confirmed zone of it. */
  onResetLine: (orderItemId: string, zoneId?: string | null) => Promise<PickActionResult>;
  /** "Zone is empty — skip to the next zone" (from the line sheet). */
  onSkipZone: (orderItemId: string, zoneId: string) => Promise<PickActionResult>;
  /** "Undo skip": every zone skipped for the line goes back into its allocation. */
  onRestoreZones: (orderItemId: string) => Promise<PickActionResult>;
  /** Quarantine → Missing (from the line sheet): the zone's stock of the line goes to quarantine. */
  onQuarantineMissing: (args: PickQuarantineMissingArgs) => Promise<PickQuarantineResult>;
  /** Quarantine → Damaged (from the line sheet): the scanned unit(s) are quarantined in place. */
  onQuarantineDamaged: (args: PickQuarantineDamagedArgs) => Promise<PickQuarantineResult>;
  onComplete: () => void;
  onRelease: () => void;
  /** Pick is complete: the result panel, with Continue to Verify and Done (home). */
  completed: PickCompletion | null;
  onContinueToVerify: () => void;
  onDone: () => void;
  /** The combined-shipment step (@/lib/shared-coms/CombineShipment); absent in isolated renders. */
  combine?: CombineStep | null;
  /** The pick path is finished (every line picked or short): the same-client alert may show. */
  combineAtEnd?: boolean;
  /** Before Complete Pick: true when it put the same-client alert up instead. */
  onBeforeComplete?: () => Promise<boolean>;
};

/**
 * What the "Pick complete" panel says about the combined shipment: where this tote goes. Null when
 * there is nothing to combine with.
 */
export const pickCompleteCombineNote = (context: CombineContext | null | undefined): string | null => {
  const partners = combinePartners(context);
  if (context?.shipment && partners.length) {
    const parent = combineParent(context);
    const where =
      parent && !parent.isCurrent
        ? `put this tote with ${soLabel(parent)}`
        : 'keep their totes with this one';
    return `Combined with ${soList(partners)} (${context.shipment.bundleNumber}) — ${where}. They are verified and shipped together.`;
  }
  const waiting = combineAlertOrders(context);
  if (waiting.length) {
    return `${soList(waiting)} for this client ${
      waiting.length === 1 ? 'is' : 'are'
    } still being picked — set this tote aside to combine with ${
      waiting.length === 1 ? 'it' : 'them'
    } at Verify.`;
  }
  return null;
};

// equasion's ButtonProps type omits onClick (it reaches the <button> through ...rest at runtime);
// a spread is not excess-property checked.
const click = (onClick: () => void) => ({ onClick });

/**
 * Pick → Verify → Ship (Option 2), step 1: PICK. The server's walk, ZONE BY ZONE: one row per zone
 * stop, so a product whose stock sits in zones 1 and 4 is a row at zone 1 and another at zone 4,
 * each picked where the walk reaches it — by scanning the zone and ONE unit (serialized: the
 * expected expiration too) and then grabbing that zone's share unscanned. Nothing is written to
 * inventory here — Verify records the units.
 *
 * One picker per order: the claim is taken on open (free, or already this picker's), blocked with
 * the holder's name while someone else's claim is live, and taken over only once the server calls
 * it stale. Every action answers with the whole list, which replaces what is on screen.
 */
function SalesOrderPick({
  list,
  company,
  companyId,
  setStartZoneId,
  busy,
  startError,
  onStart,
  onConfirmLine,
  onMarkShort,
  onMarkStopShort,
  onScanUnit,
  onRemoveUnit,
  onResetLine,
  onSkipZone,
  onRestoreZones,
  onQuarantineMissing,
  onQuarantineDamaged,
  onComplete,
  onRelease,
  completed,
  onContinueToVerify,
  onDone,
  combine = null,
  combineAtEnd = false,
  onBeforeComplete
}: SalesOrderPickProps) {
  const navigate = useNavigate();
  // The sheet is opened at one STOP of a line: its line and the zone the row sent the picker to.
  const [openSheetAt, setOpenSheetAt] = useState<{
    orderItemId: string;
    zoneId: string | null;
  } | null>(null);
  const openLineId = openSheetAt?.orderItemId ?? null;
  const [confirmShortsOpen, setConfirmShortsOpen] = useState(false);
  // A quarantine left a line no pickable stock: offer to close it (mark it short) right there.
  const [closeLinePrompt, setCloseLinePrompt] = useState<{
    orderItemId: string;
    title: string;
    text: string;
  } | null>(null);
  // A unit-scan (controlled) line's short from its row: the reason form (no quantity to type).
  const [shortReasonFor, setShortReasonFor] = useState<string | null>(null);
  const {
    salesOrderId,
    vendorSalesOrderNumber,
    clientName,
    lines = [],
    pathConfigured,
    anchorZone,
    anchorSource,
    anchorOnPath,
    direction,
    toteName
  } = list;
  const claim = pickClaimView(list);
  const mine = claim.kind === 'mine';
  const rows = pickWalkRows(list);
  const openLine = openLineId
    ? lines.find((line) => line.orderItemId === openLineId) || null
    : null;
  // The sheet's stop, read fresh from every answer. Once that zone is confirmed (or skipped) it is no
  // longer on the walk: the sheet then reads the line as it now stands.
  const openStop: PickWalkStop | null =
    (openSheetAt?.zoneId &&
      (list.stops || []).find(
        (stop) => stop.orderItemId === openLineId && stop.zone?.id === openSheetAt.zoneId
      )) ||
    null;
  // Read fresh from every answer: an Undo, a takeover or a refreshed list ends the offer.
  const closeLine = closeLinePrompt
    ? lines.find((line) => line.orderItemId === closeLinePrompt.orderItemId) || null
    : null;
  const closeLineOpen = !!closeLine && pickLineNeedsClose(closeLine) && mine;
  const shortReasonLine = shortReasonFor
    ? lines.find((line) => line.orderItemId === shortReasonFor) || null
    : null;
  const shortReasonOpen =
    !!shortReasonLine && pickLineNeedsClose(shortReasonLine) && !isPickLineLocked(shortReasonLine) && mine;
  const doneCount = list.pickedLineCount + list.shortLineCount;
  const lockedNote = pickLockedLinesNote(list);
  const summaryPath = `/sales-order-summary/${salesOrderId}`;
  // D4: an order that started Pick → Verify → Ship finishes in it even after the company is
  // switched back to the standard workflow. Say so, rather than let the picker wonder why this
  // order still has the new screens when the next one will not. Only on an explicit value: the
  // field is null for a reader the server does not treat as vendor-side, which says nothing.
  const companySwitchedBack =
    typeof company?.fulfillmentWorkflow === 'string' &&
    !companyUsesPickVerifyShip(company) &&
    !!list.workflow?.started;

  // Only the claim holder picks: losing the claim (a takeover, a release elsewhere) closes the sheet
  // under the picker, and a line that left the order (a refreshed list without it) or became locked
  // to them (the permission was taken away) closes too.
  const openLineLocked = !!openLine && isPickLineLocked(openLine);
  useEffect(() => {
    if (openLineId && (!mine || !openLine || openLineLocked)) setOpenSheetAt(null);
  }, [mine, openLine, openLineId, openLineLocked]);

  const openSheet = (line: PickLine, stop: PickWalkStop | null) => {
    if (!mine || busy) return;
    // Seen, never selected: a controlled line without the permission (the server refuses it too).
    if (isPickLineLocked(line)) {
      const copy = pickLockedLineCopy(line);
      alert(copy.title, copy.text, 6, 'info');
      return;
    }
    // A unit-scan line part-way through a zone resumes scanning there (it has no walk stop for it).
    const resumeZone = stop ? null : pickUnitScanResumeZone(line);
    if (resumeZone) {
      setOpenSheetAt({ orderItemId: line.orderItemId, zoneId: resumeZone.id });
      return;
    }
    if (!stop && (line.noStock || !line.expectedZone)) {
      const copy = pickNoStockCopy(line);
      alert(copy.title, copy.text, 6, 'info');
      return;
    }
    setOpenSheetAt({
      orderItemId: line.orderItemId,
      zoneId: stop?.zone?.id ?? line.expectedZone?.id ?? null
    });
  };

  // Nothing picked: the line short with none found. Part-picked: the REST short — the units its
  // confirmed zones gave are in the tote.
  const markShortFromRow = async (line: PickLine) => {
    // A unit-scan line's short is its units scanned, with a reason: ask for the reason first.
    if (pickLineScansUnits(line)) {
      setShortReasonFor(line.orderItemId);
      return;
    }
    const result = await onMarkShort(line.orderItemId, pickRowShortFound(line));
    if (!result.ok && !result.handled)
      alert('Could not mark short', result.message, 6, 'error');
  };

  // "Close line": the same short as the row's button, on the line as it now stands.
  const closeLineFromPrompt = async () => {
    const line = closeLine;
    setCloseLinePrompt(null);
    if (line && pickLineNeedsClose(line)) await markShortFromRow(line);
  };

  const recordShortWithReason = async (reason: string) => {
    const line = shortReasonLine;
    if (!line) return;
    const result = await onMarkShort(line.orderItemId, pickScannedCount(line), { reason });
    if (result.ok) setShortReasonFor(null);
    else if (!result.handled) alert('Could not record short', result.message, 6, 'error');
  };

  const undoRow = async (line: PickLine, zoneId?: string | null) => {
    const result = await onResetLine(line.orderItemId, zoneId);
    if (!result.ok && !result.handled)
      alert('Could not undo', result.message, 6, 'error');
  };

  const restoreZonesFromRow = async (line: PickLine) => {
    const result = await onRestoreZones(line.orderItemId);
    if (!result.ok && !result.handled)
      alert('Could not undo the skip', result.message, 6, 'error');
  };

  const completePick = async () => {
    if (!list.canComplete || busy) return;
    // Another order for this client still being picked: the alert comes first (once).
    if (onBeforeComplete && (await onBeforeComplete())) return;
    if (list.shortLineCount > 0) setConfirmShortsOpen(true);
    else onComplete();
  };
  const combineNote = completed ? pickCompleteCombineNote(combine?.context) : null;

  // Start is the gateway: it takes (or refreshes) the claim, so when it was refused every later
  // write would be too — block with its reason, even over a claim that still reads as this
  // picker's. (Controlled lines are not a reason: they are locked one by one, not at the door.)
  const claimBlocked = claim.kind === 'other' || !!startError;

  return (
    <Page className="sales-order-pick action-page">
      <div className="action-top-bar">
        <div className="order-details">
          <h1>Pick — SO #{vendorSalesOrderNumber}</h1>
          <h2>
            {doneCount} of {lines.length} lines done{clientName ? ` - ${clientName}` : ''}
          </h2>
        </div>
        <FiXCircle
          aria-label="Close"
          onClick={() => navigate(salesOrdersTabPath('picking'))}
        />
      </div>

      <div className="action-page-body">
        {pathConfigured ? (
          <div className="walk-bar">
            <FiNavigation className="walk-bar__icon" />
            <div className="walk-bar__text">
              {anchorZone && anchorSource === 'startZone' && anchorOnPath && (
                <>
                  Starting at <strong>{anchorZone.name}</strong>, walking{' '}
                  {direction === 'backward' ? 'back' : 'forward'}
                </>
              )}
              {anchorZone && anchorSource === 'firstPick' && anchorOnPath && (
                <>
                  Routed from your first pick at <strong>{anchorZone.name}</strong>
                </>
              )}
              {anchorZone && !anchorOnPath && (
                <>
                  <strong>{anchorZone.name}</strong> is not on the pick path — showing the
                  walk from its start
                </>
              )}
              {!anchorZone && <>In walk order from the start of the path</>}
            </div>
            {anchorSource === 'startZone' ? (
              <button
                type="button"
                className="walk-bar__clear"
                aria-label="Clear starting zone"
                onClick={() => setStartZoneId(null)}
              >
                <FiX />
              </button>
            ) : (
              <ZoneScanButton
                className="walk-bar__start"
                companyId={companyId}
                title="Start from a zone"
                hint="Scan the label of the zone you want to begin at. The rest of the order is routed from there."
                manualEntryLabel="Never mind — start from the beginning"
                onSelect={(zone) => setStartZoneId(zone.id)}
                disabled={!mine}
              />
            )}
          </div>
        ) : (
          <div className="walk-bar walk-bar--none">
            {/* The server's rule, as on the standard picking screen: with no zone sequenced,
                planPickPath keeps the lines in order-line order (no-stock lines last). */}
            No pick path set up for this floor yet, so lines are in order-line order. Walk
            it once from the menu: Pick Path Setup.
          </div>
        )}

        <div className="sales-order-pick__summary">
          <span>
            {list.pickedLineCount} picked · {list.shortLineCount} short ·{' '}
            {list.openLineCount} to pick
          </span>
          {toteName && (
            <span className="sales-order-pick__tote">
              <FiPackage aria-hidden /> Tote {toteName}
            </span>
          )}
        </div>

        {lockedNote && (
          <p className="sales-order-pick__note sales-order-pick__locked">
            <FiLock aria-hidden /> {lockedNote}
          </p>
        )}

        {companySwitchedBack && (
          <p className="sales-order-pick__note">
            This company now uses the standard workflow. This order was already started in
            Pick → Verify → Ship, so it finishes that way.
          </p>
        )}

        <div className="sales-order-pick__lines">
          {rows.map((row) => (
            <PickLineRow
              key={row.key}
              line={row.line}
              stop={row.stop}
              otherStops={row.otherStops}
              showLineActions={row.first}
              interactive={mine}
              onOpen={openSheet}
              onMarkShort={markShortFromRow}
              onUndo={undoRow}
              onRestoreZones={restoreZonesFromRow}
            />
          ))}
        </div>

        <div className="sales-order-pick__footer">
          {combine && mine && (
            <CombineShipmentStep
              step={combine}
              variant="pick"
              showAlert={combineAtEnd}
              onOpenOrder={(order) => navigate(combineOrderPath(order))}
            />
          )}
          {pickShowAddTote(list) && (
            <Button
              type="primary"
              fullWidth
              disabled={!mine}
              {...click(() => navigate(addTotePathForPick(salesOrderId)))}
            >
              Add Tote
            </Button>
          )}
          <Button
            type="primary"
            fullWidth
            // THE gate is the server's: `canComplete` is computed with the same rules
            // completeSalesOrderPick enforces (every line picked or short, something found, a tote
            // when totes are on), and the reason it gives is shown verbatim.
            disabled={!mine || !list.canComplete || !!busy}
            {...click(completePick)}
          >
            Complete Pick
          </Button>
          {mine && !list.canComplete && list.completeBlockedReason && (
            <p className="sales-order-pick__blocked">{list.completeBlockedReason}</p>
          )}
          {mine && (
            <Button type="secondary" fullWidth disabled={!!busy} {...click(onRelease)}>
              Release
            </Button>
          )}
        </div>
      </div>

      <div
        className={[
          'result-panel',
          (!!completed || confirmShortsOpen || closeLineOpen || shortReasonOpen || claimBlocked) &&
            'open'
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {completed && (
          <>
            <div className="result-message">
              <FiCheckCircle style={{ color: 'var(--green)' }} />
              <h1>Pick complete</h1>
              <p>{pickCompleteText(completed)}</p>
              {combineNote && <p className="sales-order-pick__combined-note">{combineNote}</p>}
            </div>
            <div className="result-actions">
              <Button type="primary" fullWidth {...click(onContinueToVerify)}>
                Continue to Verify
              </Button>
              <Button type="secondary" fullWidth {...click(onDone)}>
                Done
              </Button>
            </div>
          </>
        )}
        {!completed && shortReasonOpen && !confirmShortsOpen && !claimBlocked && (
          <PickShortReasonForm
            line={shortReasonLine!}
            busy={!!busy}
            onSubmit={(reason) => void recordShortWithReason(reason)}
            onCancel={() => setShortReasonFor(null)}
          />
        )}
        {!completed && closeLineOpen && !shortReasonOpen && !confirmShortsOpen && !claimBlocked && (
          <>
            <div className="result-message">
              <FiAlertTriangle style={{ color: 'var(--orange)' }} />
              <h1>{closeLinePrompt!.title}</h1>
              <p>{closeLinePrompt!.text}</p>
              <p>The unfound quantity is cancelled when Pick is completed.</p>
            </div>
            <div className="result-actions">
              <Button
                type="primary"
                fullWidth
                disabled={!!busy}
                {...click(() => void closeLineFromPrompt())}
              >
                {pickCloseLineLabel(closeLine!)}
              </Button>
              <Button type="secondary" fullWidth {...click(() => setCloseLinePrompt(null))}>
                Not now
              </Button>
            </div>
          </>
        )}
        {!completed && confirmShortsOpen && !claimBlocked && (
          <>
            <div className="result-message">
              <FiAlertTriangle style={{ color: 'var(--orange)' }} />
              <h1>Complete with shorts?</h1>
              <p>{pickShortsConfirmText(list.shortLineCount)}</p>
            </div>
            <div className="result-actions">
              <Button
                type="primary"
                fullWidth
                disabled={!!busy}
                {...click(() => {
                  setConfirmShortsOpen(false);
                  onComplete();
                })}
              >
                Complete Pick
              </Button>
              <Button
                type="secondary"
                fullWidth
                {...click(() => setConfirmShortsOpen(false))}
              >
                Go back
              </Button>
            </div>
          </>
        )}
        {!completed && claim.kind === 'other' && (
          <>
            <div className="result-message">
              <FiLock style={{ color: 'var(--orange)' }} />
              <h1>Being picked by {claim.name}</h1>
              <p>
                Last active {claim.minutesIdle} min ago.{' '}
                {claim.stale
                  ? 'Their pick has been idle long enough to take over.'
                  : `It can be taken over once it has been idle ${STALE_PICK_CLAIM_MINUTES} min.`}
              </p>
            </div>
            <div className="result-actions">
              <Button
                type="primary"
                fullWidth
                disabled={!claim.stale || !!busy}
                {...click(() => onStart(true))}
              >
                Take over
              </Button>
              <Button type="secondary" fullWidth {...click(() => navigate(summaryPath))}>
                Back
              </Button>
            </div>
          </>
        )}
        {!completed && claim.kind !== 'other' && !!startError && (
          <>
            <div className="result-message">
              <FiAlertTriangle style={{ color: 'var(--red)' }} />
              <h1>Could not start Pick</h1>
              <p>{startError}</p>
            </div>
            <div className="result-actions">
              <Button
                type="primary"
                fullWidth
                disabled={!!busy}
                {...click(() => onStart(false))}
              >
                Try again
              </Button>
              <Button type="secondary" fullWidth {...click(() => navigate(summaryPath))}>
                Back
              </Button>
            </div>
          </>
        )}
      </div>

      {openLine && mine && !openLineLocked && (
        <PickLineSheet
          // A new stop is a new pick: fresh steps, never the last zone's accepted label.
          key={`${openSheetAt?.orderItemId}:${openSheetAt?.zoneId ?? ''}`}
          line={pickLineAtStop(openLine, openStop)}
          stop={openStop}
          stopZoneId={openSheetAt?.zoneId ?? null}
          stops={list.stops ?? null}
          companyId={companyId}
          onClose={() => setOpenSheetAt(null)}
          onConfirmLine={onConfirmLine}
          onMarkShort={onMarkShort}
          onMarkStopShort={onMarkStopShort}
          onScanUnit={onScanUnit}
          onRemoveUnit={onRemoveUnit}
          onSkipZone={onSkipZone}
          onQuarantineMissing={onQuarantineMissing}
          onQuarantineDamaged={onQuarantineDamaged}
          onNoStockLeft={(line, copy) =>
            setCloseLinePrompt({ orderItemId: line.orderItemId, ...copy })
          }
        />
      )}

      {!!busy && (
        <div className="blocking-spinner-container" onClick={(e) => e.stopPropagation()}>
          <FadeLoader color="var(--n0)" />
        </div>
      )}
    </Page>
  );
}

export default SalesOrderPick;
